import React, { useMemo } from "react";
import {
  UserOutlined,
  FileTextOutlined,
  MessageOutlined,
  TeamOutlined,
  SafetyOutlined,
  BarChartOutlined,
  SettingOutlined,
  PictureOutlined,
  RobotOutlined,
  DollarOutlined,
  ApiOutlined,
  SecurityScanOutlined,
  PicLeftOutlined,
  FormOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import CommionPermissonsTab from "./CommionPermissonsTab";
import CommionPermissonsAcc from "./CommionPermissonsAcc";

const CommionPermissons = ({
  permission,
  UserPermission,
  handlePermissionChange,
  existPermission,
  selectAll,
  view = "Accordion",
}) => {
  // Define permission groups with their patterns, icons, and colors
  const permissionGroups = useMemo(
    () => ({
      user: {
        name: "User Management",
        icon: <UserOutlined />,
        color: "blue",
        patterns: [
          "user",
          "profile",
          "impersonate",
          "edit profile",
          "change password",
        ],
        description: "User account and profile management",
      },
      campaign: {
        name: "Campaign Management",
        icon: <FormOutlined />,
        color: "green",
        patterns: ["campaign", "schedule"],
        description: "Marketing campaigns and scheduling",
      },
      template: {
        name: "Template Management",
        icon: <FileTextOutlined />,
        color: "orange",
        patterns: ["template"],
        description: "Message template creation and management",
      },
      carousel: {
        name: "Carousel Management",
        icon: <PicLeftOutlined />,
        color: "orange",
        patterns: ["carousel"],
        description: "Carousel template creation and management",
      },
      flows: {
        name: "Meta Flows Management",
        icon: <ApartmentOutlined />,
        color: "geekblue",
        patterns: ["flow"],
        description: "Meta Flows creation and management",
      },
      message: {
        name: "Messaging",
        icon: <MessageOutlined />,
        color: "purple",
        patterns: ["message", "incoming", "outgoing"],
        description: "Message sending and receiving",
      },

      contact: {
        name: "Contact Management",
        icon: <TeamOutlined />,
        color: "cyan",
        patterns: ["contact", "group"],
        description: "Contact and group management",
      },
      chatbot: {
        name: "Chatbot & Automation",
        icon: <RobotOutlined />,
        color: "magenta",
        patterns: ["chatbot", "automation", "webhook"],
        description: "Automated responses and bot management",
      },
      report: {
        name: "Reports & Analytics",
        icon: <BarChartOutlined />,
        color: "gold",
        patterns: ["report", "analysis", "cost analysis", "history"],
        description: "Analytics and reporting features",
      },
      media: {
        name: "Media Management",
        icon: <PictureOutlined />,
        color: "lime",
        patterns: ["media", "upload"],
        description: "File and media management",
      },
      billing: {
        name: "Billing & Finance",
        icon: <DollarOutlined />,
        color: "red",
        patterns: ["billing", "credit", "payment", "pricing"],
        description: "Financial and billing operations",
      },
      security: {
        name: "Security & Credentials",
        icon: <SecurityScanOutlined />,
        color: "volcano",
        patterns: ["security", "credential", "meta credential", "encrypt"],
        description: "Security settings and credentials",
      },
      role: {
        name: "Roles & Permissions",
        icon: <SafetyOutlined />,
        color: "geekblue",
        patterns: ["role", "permission", "assign"],
        description: "Access control and permissions",
      },
      settings: {
        name: "System Settings",
        icon: <SettingOutlined />,
        color: "default",
        patterns: ["setting", "config", "alerts"],
        description: "Application configuration and settings",
      },
      api: {
        name: "Developer & API",
        icon: <ApiOutlined />,
        color: "processing",
        patterns: ["api", "webhook", "developer"],
        description: "API access and developer tools",
      },
    }),
    []
  );

  // Group permissions by category
  const groupedPermissions = useMemo(() => {
    const grouped = {};
    const ungrouped = [];

    // Initialize groups
    Object.keys(permissionGroups).forEach((key) => {
      grouped[key] = [];
    });

    permission?.forEach((perm) => {
      const permName = perm.name.toLowerCase();
      let isGrouped = false;

      // Check which group this permission belongs to
      Object.entries(permissionGroups).forEach(([groupKey, groupConfig]) => {
        if (
          groupConfig.patterns.some((pattern) => permName.includes(pattern))
        ) {
          grouped[groupKey].push(perm);
          isGrouped = true;
        }
      });

      if (!isGrouped) {
        ungrouped.push(perm);
      }
    });

    // Add ungrouped permissions to a misc category
    if (ungrouped.length > 0) {
      grouped.misc = ungrouped;
    }

    return grouped;
  }, [permission, permissionGroups]);

  // Calculate statistics for each group
  const getGroupStats = (groupKey) => {
    const groupPermissions = groupedPermissions[groupKey] || [];
    const totalPermissions = groupPermissions.length;
    const selectedPermissions = groupPermissions.filter(
      (perm) => existPermission?.includes(perm?.id) || selectAll
    ).length;

    return { total: totalPermissions, selected: selectedPermissions };
  };

  // Handle group selection (select/deselect all permissions in a group)
  const handleGroupSelect = (groupKey, checked) => {
    const groupPermissions = groupedPermissions[groupKey] || [];
    groupPermissions.forEach((perm) => {
      const event = {
        target: {
          value: perm.id,
          checked: checked,
        },
      };
      handlePermissionChange(event);
    });
  };
  // console.log('view:', view);
  if (view === "Accordion") {
    return <CommionPermissonsAcc
      permission={permission}
      UserPermission={UserPermission}
      handlePermissionChange={handlePermissionChange}
      existPermission={existPermission}
      selectAll={selectAll}
      getGroupStats={getGroupStats}
      handleGroupSelect={handleGroupSelect}
      permissionGroups={permissionGroups}
      groupedPermissions={groupedPermissions}
    />;
  } else {
    return <CommionPermissonsTab
      permission={permission}
      UserPermission={UserPermission}
      handlePermissionChange={handlePermissionChange}
      existPermission={existPermission}
      selectAll={selectAll}
      getGroupStats={getGroupStats}
      handleGroupSelect={handleGroupSelect}
      permissionGroups={permissionGroups}
      groupedPermissions={groupedPermissions}
    />;
  }
};

export default CommionPermissons;
